import {
  IMAGE_LOADER,
  ImageLoaderConfig,
  isPlatformServer,
} from '@angular/common';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  isDevMode,
  inject,
  TransferState,
  provideAppInitializer,
  provideZoneChangeDetection,
  PLATFORM_ID,
} from '@angular/core';
import {
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
  withNoHttpTransferCache,
} from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  Router,
  withComponentInputBinding,
  withDisabledInitialNavigation,
  withInMemoryScrolling,
} from '@angular/router';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import {
  LOCAL_STORAGE_ENGINE,
  NgxsStoragePluginModule,
  SESSION_STORAGE_ENGINE,
} from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { ApiModule } from 'api';
import {
  LocalStorageModule,
  paramsFnInterceptor,
  CommonModule as SSCommonModule,
} from 'common';
import { appRoutes } from './app.routes';
import { WwwDirectusService } from 'www-cms';
import {
  WWWConfig,
  WWW_CONFIG,
  WWW_CONFIG_STATE_KEY,
} from './shared/models/config.model';
import { ShellState, UserPrefState } from './shell/shell.state';
import { TicketingCartState } from './ticketing/ticketing-cart.state';
import { GiftCardCart } from 'gift-cards';
import { Angulartics2Module } from 'angulartics2';
import { chunkErrorHandlerInterceptor } from './chunk-error-handler.interceptor';
import { NgxsRouterPluginModule, RouterState } from '@ngxs/router-plugin';
import { PageState } from './page/state/page.state';
import { cookieInterceptor } from './cookie.interceptor';
import { provideWaf, wafInterceptor } from 'waf';
import { generateDynamicRoutes } from './app.initializers';
import { SEGMENT_CONFIG } from 'segment';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import {
  provideDirectus,
  directusCacheSsrInterceptor,
  generateImageUrl,
} from 'directus';

export const getAppConfig = (csrConfig?: WWWConfig): ApplicationConfig => ({
  providers: [
    {
      provide: WWW_CONFIG,
      useFactory: () =>
        inject(TransferState).get(WWW_CONFIG_STATE_KEY, csrConfig),
    },
    {
      provide: SEGMENT_CONFIG,
      useFactory: () => inject(WWW_CONFIG).segment,
    },
    provideEnvironmentNgxMask(),
    provideClientHydration(
      withEventReplay(),
      isDevMode() ? withNoHttpTransferCache() : withHttpTransferCacheOptions({})
    ),
    provideZoneChangeDetection(),
    provideRouter(
      appRoutes,
      // Router's automatic initial navigation is disabled so it cannot race
      // with the `generateDynamicRoutes` APP_INITIALIZER below. That initializer
      // fetches CMS page URLs and calls `router.resetConfig`; it must complete
      // before navigation starts, otherwise newly-created CMS pages fall
      // through to the `**` wildcard and 302 to /not-found on first render.
      // `router.initialNavigation()` is invoked manually at the end of
      // `generateDynamicRoutes` to kick off navigation with the final config.
      withDisabledInitialNavigation(),
      withComponentInputBinding(),
      withInMemoryScrolling({
        // Disabled because custom implementation is used
        // in app component
        anchorScrolling: 'disabled',
        scrollPositionRestoration: 'disabled',
      })
    ),
    provideAnimations(),
    provideWaf(() => inject(WWW_CONFIG).wafConfig),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        paramsFnInterceptor,
        directusCacheSsrInterceptor,
        chunkErrorHandlerInterceptor,
        cookieInterceptor,
        wafInterceptor,
      ])
    ),
    {
      provide: IMAGE_LOADER,
      useFactory: () => {
        const wwwConfig = inject(WWW_CONFIG);
        return (imageLoaderConfig: ImageLoaderConfig) => {
          // This is the configuration for the directus image optimization
          // See https://docs.directus.io/reference/files.html#custom-transformations
          // Basically, directus uses node sharp to resize the image on the server
          // based on the width and other config options passed in the url.
          return generateImageUrl(imageLoaderConfig, wwwConfig.baseAssetUrl);
        };
      },
    },
    provideDirectus<WwwDirectusService>(WwwDirectusService, () => {
      const config = inject(WWW_CONFIG);

      return {
        baseUrl: config.directusBaseUrl,
        instanceUrl: 'https://sight-sound.directus.app',
        showDrafts: config.showDirectusDrafts,
      };
    }),
    provideAppInitializer(() => {
      const initializerFn = generateDynamicRoutes(
        inject(WwwDirectusService),
        inject(Router),
        isPlatformServer(inject(PLATFORM_ID))
      );
      return initializerFn();
    }),
    importProvidersFrom([
      SSCommonModule.forRoot({
        useFactory: () => ({
          featureFlags: inject(WWW_CONFIG).featureFlags,
        }),
      }),
      LocalStorageModule.forRoot({
        prefix: 'sightsound.www',
        storageType: 'localStorage',
      }),
      ApiModule.forRoot({
        useFactory: () => ({
          heliosUrl: inject(WWW_CONFIG).heliosUrl,
          isHelios: false,
        }),
      }),
      NgxsModule.forRoot(
        [
          ShellState,
          UserPrefState,
          GiftCardCart.GiftCardCartState,
          TicketingCartState,
          PageState,
        ],
        {
          developmentMode: isDevMode(),
          compatibility: {
            strictContentSecurityPolicy: true,
          },
        }
      ),
      NgxsRouterPluginModule.forRoot(),
      NgxsReduxDevtoolsPluginModule.forRoot({
        disabled: !isDevMode(),
      }),
      NgxsStoragePluginModule.forRoot({
        keys: [
          {
            key: UserPrefState,
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: ShellState,
            engine: SESSION_STORAGE_ENGINE,
          },
          {
            key: RouterState,
            engine: SESSION_STORAGE_ENGINE,
          },
          {
            key: 'giftCardCart',
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: TicketingCartState,
            engine: LOCAL_STORAGE_ENGINE,
          },
        ],
      }),
      Angulartics2Module.forRoot(),
    ]),
  ],
});
